export type Rag = 'verde' | 'amarelo' | 'vermelho' | 'sem-meta'

export const PRESETS = ['hoje', 'semana', 'mes', 'mes_passado', 'ano'] as const
export type Preset = (typeof PRESETS)[number]

export const ROTULO_PRESET: Record<Preset, string> = {
  hoje: 'Hoje',
  semana: '7 dias',
  mes: 'Mês atual',
  mes_passado: 'Mês passado',
  ano: 'Ano',
}

export interface MetaDoPeriodo {
  meta: number
  atingimento: number
  rag: Rag
}

export interface Panorama {
  receitaLiquida: number
  receitaBruta: number
  desconto: number
  descontoPct: number
  nVendas: number
  ticketMedio: number
  ticketMediano: number
  cortesias: number
  nRegistrosSemValor: number
}

export interface FaixaTicket {
  faixa: string
  negocios: number
  receita: number
}

export interface CorteReceita {
  label: string
  receita: number
  negocios: number
}

export interface LinhaVendedor extends MetaDoPeriodo {
  vendedorId: string | null
  vendedor: string
  receitaLiquida: number
  desconto: number
  negocios: number
  ticketMedio: number
}

/** Uma venda do período com a ficha completa — a linha da tabela por executivo. */
export interface VendaDetalhada {
  id: string
  vendedor: string
  nome: string
  empresa: string | null
  produtos: string[]
  valorLiquido: number
  desconto: number | null
  meioPagamento: string | null
  parcelas: number
  fechadoEm: string
}

/** Pipeline em aberto de um executivo: potencial cheio e forecast ponderado. */
export interface ForecastVendedor {
  vendedor: string
  negocios: number
  potencial: number
  ponderado: number
}

export interface Recebimento {
  meio: string
  valor: number
  pct: number
}

export interface EstagioForecast {
  estagio: string
  negocios: number
  valor: number
  ponderado: number
  probabilidade: number
}

export interface Forecast {
  negocios: number
  potencial: number
  ponderado: number
  cenarios: { conservador: number; realista: number; agressivo: number }
  estagios: EstagioForecast[]
}

export interface NegocioPendente {
  id: string
  nome: string
  url: string
  vendedor: string
  valor: number
}

export interface Pendencia {
  campo: string
  rotulo: string
  impacto: string
  negocios: number
  receitaAfetada: number
  porResponsavel: { vendedor: string; negocios: number }[]
  itens: NegocioPendente[]
}

export interface SemanaFechada {
  /** Faixa de datas da semana, ex.: "01/08". */
  label: string
  receita: number
  negocios: number
}

/** Um ponto da série semanal por trás de um "médio" (desconto, ciclo). */
export interface PontoSemanal {
  /** Rótulo da semana (dia/mês do início), ex.: "01/08". */
  rotulo: string
  valor: number
}

export interface Relatorio {
  /** Falso quando o HubSpot recusou a leitura e não há snapshot anterior. */
  disponivel: boolean
  /** O que precisa ser feito; vazio quando a coleta foi normal. */
  motivo: string
  /** Números vindos do último snapshot, não de coleta nova — mostrar o aviso. */
  desatualizado: boolean
  /** ISO da coleta que gerou os números; `null` quando não há dado algum. */
  coletadoEm: string | null
  periodo: { inicio: string; fim: string; label: string }
  panorama: Panorama
  bookings: MetaDoPeriodo
  forecastVsMeta: MetaDoPeriodo
  faixasDeTicket: FaixaTicket[]
  porProduto: CorteReceita[]
  porCanal: CorteReceita[]
  porVendedor: LinhaVendedor[]
  /** A ficha completa de cada venda do período, agrupável por executivo. */
  vendasDetalhadas: VendaDetalhada[]
  recebimentos: Recebimento[]
  forecast: Forecast
  /** Pipeline em aberto quebrado por executivo (potencial + ponderado). */
  forecastPorVendedor: ForecastVendedor[]
  /** Receita fechada semana a semana dentro do período selecionado. */
  semanas: SemanaFechada[]
  /** Ciclo médio de venda (dias entre criação e fechamento do negócio). */
  cicloMedioDias: number
  /** Desconto médio por venda, semana a semana — a série por trás do médio. */
  descontoSemanal: PontoSemanal[]
  /** Ciclo médio de venda, semana a semana — a série por trás do médio. */
  cicloSemanal: PontoSemanal[]
  pendencias: Pendencia[]
  registrosSemValor: { total: number; itens: { id: string; nome: string; produtos: string[] }[] }
}

export interface Leads {
  /** Conversão MQL → cliente no período. `taxa` é null quando não houve MQL. */
  conversaoMql: { mqls: number; clientes: number; taxa: number | null }
  /** Leads (contatos criados no período) por canal, do maior pro menor. */
  porCanal: { canal: string; total: number }[]
}

export interface Cac {
  /** Falso quando não há centro de custo de marketing configurado. */
  configurado: boolean
  novosClientes: number
  investimento: number
  cac: number | null
  centros: string[]
}

export interface PontoSerie {
  mes: string
  bookings: number
  negocios: number
  meta: number
  atingimento: number
  rag: Rag
}

/** Um dos três níveis da meta anual (1 = base, 3 = a mais ambiciosa). */
export interface NivelAnual {
  nivel: number
  meta: number
  atingimento: number
  runRate: number
  atingimentoProjetado: number
  rag: Rag
}

export interface ProgressoAnual {
  ano: number
  acumulado: number
  runRate: number
  mesesDecorridos: number
  niveis: NivelAnual[]
}

export interface SerieHistorica {
  pontos: PontoSerie[]
  progressoAnual: ProgressoAnual
}

export interface MetasAnuais {
  ano: number
  niveis: { nivel: number; valor: number }[]
}

export interface MetasDoMes {
  mes: string
  empresa: number
  vendedores: { vendedorId: string; nome: string; valor: number }[]
}

export const CAMPOS_ORDENACAO_NEGOCIO = [
  'fechadoEm',
  'valor',
  'desconto',
  'cortesias',
  'nome',
  'empresa',
  'vendedor',
] as const

export type CampoOrdenacaoNegocio = (typeof CAMPOS_ORDENACAO_NEGOCIO)[number]

export interface NegocioListado {
  id: string
  nome: string
  empresa: string | null
  url: string
  valor: number
  desconto: number | null
  cortesias: number
  fechadoEm: string
  vendedor: string
  produtos: string[]
  canal: string | null
  meioPagamento: string | null
  parcelas: number
  faltando: string[]
}

export interface OpcoesDeFiltro {
  vendedores: string[]
  produtos: string[]
  canais: string[]
  meiosPagamento: string[]
}

export interface ListaDeNegocios {
  data: NegocioListado[]
  page: number
  perPage: number
  total: number
  totalPages: number
  opcoes: OpcoesDeFiltro
  resumo: { valor: number; desconto: number; cortesias: number }
}
